import React, { useState, useMemo } from 'react';
import { CreateUserRequest, UserRole } from '../../types/user.types';
import { PermissionsSelector } from './PermissionsSelector';
import { NotificationPreferences } from './NotificationPreferences';

const passwordRules = [
  { label: 'At least 12 characters', test: (p: string) => p.length >= 12 },
  { label: 'Uppercase letter (A-Z)', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Lowercase letter (a-z)', test: (p: string) => /[a-z]/.test(p) },
  { label: 'Number (0-9)', test: (p: string) => /[0-9]/.test(p) },
  { label: 'Special character (!@#$%^&*)', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userData: CreateUserRequest) => Promise<void>;
  loading: boolean;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
}) => {
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: '',
    fullName: '',
    role: 'approver',
    phoneNumber: '',
    department: '',
    permissions: [],
    notificationPreferences: {
      notifyOnCustomerRegistration: false,
      notifyOnSupplierRegistration: false,
      notifyOnApproval: true,
      notifyOnRejection: true,
      emailEnabled: true,
    },
    temporaryPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);

  const passwordChecks = useMemo(
    () => passwordRules.map(r => ({ ...r, passed: r.test(formData.temporaryPassword) })),
    [formData.temporaryPassword]
  );
  const allPasswordRulesPassed = passwordChecks.every(c => c.passed);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.fullName) newErrors.fullName = 'Full name is required';
    if (formData.permissions.length === 0) newErrors.permissions = 'At least one permission is required';
    if (!formData.temporaryPassword) {
      newErrors.temporaryPassword = 'Password is required';
    } else if (!allPasswordRulesPassed) {
      newErrors.temporaryPassword = 'Password does not meet all requirements';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form
      setFormData({
        email: '',
        fullName: '',
        role: 'approver',
        phoneNumber: '',
        department: '',
        permissions: [],
        notificationPreferences: {
          notifyOnCustomerRegistration: false,
          notifyOnSupplierRegistration: false,
          notifyOnApproval: true,
          notifyOnRejection: true,
          emailEnabled: true,
        },
        temporaryPassword: '',
      });
      setErrors({});
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fadeIn">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
            <h2 className="text-2xl font-bold text-gray-900">Create New User</h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent ${
                    errors.fullName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
                {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => {
                    const newRole = e.target.value as UserRole;
                    let newPermissions = formData.permissions;
                    if (newRole === 'viewer') {
                      newPermissions = ['view_only'];
                    } else if (newRole === 'approver') {
                      newPermissions = formData.permissions.filter(p => p !== 'view_only');
                      if (newPermissions.length === 0) {
                        newPermissions = ['approve_customers', 'approve_suppliers'];
                      }
                    } else if (newRole === 'admin') {
                      newPermissions = ['approve_customers', 'approve_suppliers', 'manage_users'];
                    }
                    setFormData({ ...formData, role: newRole, permissions: newPermissions });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value="admin">Admin</option>
                  <option value="approver">Approver</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  disabled={loading}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  disabled={loading}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.temporaryPassword}
                    onChange={(e) => {
                      setFormData({ ...formData, temporaryPassword: e.target.value });
                      if (errors.temporaryPassword) {
                        setErrors(prev => { const { temporaryPassword, ...rest } = prev; return rest; });
                      }
                    }}
                    className={`w-full px-4 py-2 pr-20 border rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent ${
                      errors.temporaryPassword ? 'border-red-500' : formData.temporaryPassword && allPasswordRulesPassed ? 'border-green-500' : 'border-gray-300'
                    }`}
                    disabled={loading}
                    placeholder="Enter a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {formData.temporaryPassword && (
                  <div className="mt-2 space-y-1">
                    {passwordChecks.map((check, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={check.passed ? 'text-green-600' : 'text-gray-400'}>
                          {check.passed ? '\u2713' : '\u2717'}
                        </span>
                        <span className={check.passed ? 'text-green-700' : 'text-gray-500'}>
                          {check.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {!formData.temporaryPassword && (
                  <p className="text-gray-500 text-xs mt-1">
                    Must include: uppercase, lowercase, number, and special character
                  </p>
                )}
                {errors.temporaryPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.temporaryPassword}</p>
                )}
              </div>
            </div>

            {/* Permissions */}
            <div>
              <PermissionsSelector
                selectedPermissions={formData.permissions}
                onChange={(permissions) => setFormData({ ...formData, permissions })}
                disabled={loading}
                role={formData.role}
              />
              {errors.permissions && (
                <p className="text-red-500 text-sm mt-1">{errors.permissions}</p>
              )}
            </div>

            {/* Notification Preferences */}
            <div>
              <NotificationPreferences
                preferences={formData.notificationPreferences || {}}
                onChange={(notificationPreferences) =>
                  setFormData({ ...formData, notificationPreferences })
                }
                disabled={loading}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
